import { styled } from '@mui/material';
import { useAtomValue } from 'jotai';
import { ColDef } from 'ag-grid-community';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AgTable } from '../../../../../components/AgTable/AgTable';
import { Chart, ChartSizes } from '../../../../../components/Charts/chart-utils';
import { ChartTitleWrapper } from '../../../../../components/Charts/ChartTitleWrapper';
import { ChartWrapper } from '../../../../../components/Charts/ChartWrapper/ChartWrapper';
import { IconTypes } from '../../../../../components/Icon';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { CHART_COMPONENTS } from '../../../../../types';
import { useCompanyMapLookup } from '../../../../../services/hooks/useSaveCompanyData';
import { selectedViewDatePFJ } from '../../../state/PageStateJ';
import { IPortfolioChartProps } from '../ChartComponent';
import { allChartItems } from '../ChartsSection';
import { computeData } from './MetricsByUserUtils';
import { useMetricsByUserColDefs } from './useMetricsByUserColDefs';

const TableContainer = styled('div')`
  overflow: auto;
  height: 100%;
  width: 100%;
`;

export function MetricsByUserChart(props: IPortfolioChartProps<IMetricsDataModel>) {
  const [showMore, setShowMore] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [widthState, setWidthState] = useState<string | number>(640);
  const [size, setSize] = useState('1/2 Screen');
  const ref = useRef(null);
  const date = useAtomValue(selectedViewDatePFJ);
  const columnDefs = useMetricsByUserColDefs();
  const getCompany = useCompanyMapLookup();

  const metrics = useMemo(() => {
    return computeData(props.filteredMetrics, date, getCompany);
  }, [date, getCompany, props.filteredMetrics]);

  useEffect(() => {
    const updateSize = () => {
      setWidthState(ChartSizes[size as keyof typeof ChartSizes]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [size]);

  return (
    <ChartWrapper
      id={CHART_COMPONENTS.METRICS_BY_USER}
      onMouseLeave={() => {
        setShowMore(false);
        setIsHovering(false);
      }}
      onMouseEnter={() => setIsHovering(true)}
      dataTestid={'mostRecentRoundsChart'}
      width={widthState}
    >
      <ChartTitleWrapper
        id={CHART_COMPONENTS.METRICS_BY_USER}
        isHovering={isHovering}
        title={allChartItems.get(CHART_COMPONENTS.METRICS_BY_USER)?.value ?? 'Metrics by User'}
        showMore={showMore}
        setShowMore={setShowMore}
        icon={IconTypes.STATISTICS_1}
        handleSizeChange={setSize}
        size={size}
        refProp={ref}
        isFullScreen={props.isFullScreen}
        handleFullScreen={props.onOpenFullScreen}
      />
      <Chart width={'100%'} height={props.isFullScreen ? '100%' : undefined} ref={ref}>
        <TableContainer>
          <AgTable
            columnDefs={columnDefs}
            rowData={metrics}
            rowGroupPanelShow='never'
            defaultColDef={defaultColDef}
          />
        </TableContainer>
      </Chart>
    </ChartWrapper>
  );
}

const defaultColDef: ColDef = {
  flex: 1,
};
